<script lang="ts">
	import Portal from 'svelte-portal'
	import { type MouseEventHandler } from 'svelte/elements'
	import { fly } from 'svelte/transition'

	interface Props {
		visible: boolean
		tooltipElement: HTMLElement
		autoWidth: boolean
		left: boolean
		children?: import('svelte').Snippet
		onmouseenter?: MouseEventHandler<HTMLDivElement>
		onmouseleave?: MouseEventHandler<HTMLDivElement>
	}

	let {
		visible = $bindable(),
		tooltipElement,
		autoWidth,
		left,
		children,
		onmouseenter,
		onmouseleave,
	}: Props = $props()

	let toolTipPosition: DOMRect = $state(null)
	let windowWidth = $state(window.innerWidth)

	function updateTooltipPosition() {
		toolTipPosition = tooltipElement.getBoundingClientRect()
		windowWidth = window.innerWidth
	}

	let tooltipContentWidth: number = $state(null)
	let tooltipContentHeight: number = $state(null)

	updateTooltipPosition()

	const saveZone = 8

	function calculateLeftPosition(tooltipPlaceholderLeft: number, tooltipContentWidth: number) {
		if (left) {
			return -saveZone
		} else {
			const leftPosition = -tooltipContentWidth / 2
			const maxLeft = leftPosition

			const tooltipLeftInViewport =
				tooltipPlaceholderLeft + tooltipElement.clientWidth / 2 + maxLeft
			const tooltipRightInViewport = tooltipLeftInViewport + tooltipContentWidth
			const overflowRight = tooltipRightInViewport - windowWidth

			if (overflowRight > 0) {
				const adjustedLeft = maxLeft - overflowRight - saveZone
				return Math.max(adjustedLeft, -tooltipPlaceholderLeft)
			} else if (tooltipPlaceholderLeft - tooltipContentWidth / 2 < 0) {
				return tooltipPlaceholderLeft - tooltipContentWidth / 2 + saveZone
			} else {
				return maxLeft
			}
		}
	}

	function calculateArrowLeftPosition(tooltipPlaceholderLeft: number, tooltipContentWidth: number) {
		const adjustedLeft = calculateLeftPosition(tooltipPlaceholderLeft, tooltipContentWidth)
		const arrowLeftPosition = -adjustedLeft
		return Math.min(arrowLeftPosition, tooltipContentWidth - 10) // Adjust 10 as needed
	}

	let isBottom = $derived(toolTipPosition.top - tooltipContentHeight < 0)
</script>

<Portal target="#root">
	<div
		style:left="{left
			? toolTipPosition.left - tooltipContentWidth
			: toolTipPosition.left + tooltipElement.clientWidth / 2}px"
		style:top="{left
			? toolTipPosition.top - tooltipContentHeight / 2
			: toolTipPosition.top + (isBottom ? tooltipElement.clientHeight : 0)}px"
		class="fixed z-30"
		hidden={!visible}
		transition:fly={{ y: -10, duration: 200 }}
	>
		<div
			style:left="{calculateLeftPosition(toolTipPosition.left, tooltipContentWidth)}px"
			class="
					absolute {isBottom || left ? 'top-full mt-4' : 'bottom-full mb-4'}
					{autoWidth ? '' : 'min-w-[260px]'}
					rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm drop-shadow-md dark:border-gray-700 dark:bg-gray-700
				"
			{onmouseenter}
			{onmouseleave}
			role="tooltip"
			bind:clientWidth={tooltipContentWidth}
			bind:clientHeight={tooltipContentHeight}
		>
			{@render children?.()}

			<div
				style={!left
					? `left: ${calculateArrowLeftPosition(toolTipPosition.left, tooltipContentWidth)}px`
					: ''}
				class="absolute {left
					? 'arrow-left top-1/2 -right-3 -translate-y-1/2'
					: isBottom
						? 'arrow-bottom -top-3 -translate-x-1/2'
						: 'arrow-top -bottom-3 -translate-x-1/2'} h-[16px] w-[17px] bg-white dark:bg-gray-700"
			></div>
		</div>
	</div>
</Portal>

<svelte:window
	onresize={() => (windowWidth = window.innerWidth)}
	onscrollcapture={updateTooltipPosition}
/>

<style lang="postcss">
	.arrow-top {
		clip-path: polygon(100% 0, 0 0, 50% 100%);
	}

	.arrow-bottom {
		clip-path: polygon(100% 100%, 0 100%, 50% 0);
	}

	.arrow-left {
		clip-path: polygon(100% 50%, 0 0, 0 100%);
	}
</style>
